import './ErrorBoundary.less';
import { ReactNode, ErrorInfo, useState } from 'react';
import { Button, Result, Modal, Typography, Space } from 'antd';
import { ReloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';

import { trackException } from '../../utils/monitoring';
import { Notify } from '../../services/notificationService';
import { isNetworkError } from '../../utils/errorHandling';

const { Text, Paragraph } = Typography;

const ErrorField = ({
  title,
  content,
}: {
  title: string;
  content?: string;
}) => {
  if (!content) return null;
  return (
    <div className="error-field">
      <Text strong>{title}:</Text>
      <div className="error-content">
        <Paragraph code copyable>
          {content}
        </Paragraph>
      </div>
    </div>
  );
};

interface ErrorDetails {
  message: string;
  notify: boolean;
  showModal: boolean;
  originMessage: string;
  errorStack?: string | null;
  componentStack?: string | null;
}

const _processError = (
  error: Error,
  errorInfo: ErrorInfo | null
): ErrorDetails => {
  const base = {
    errorStack: error.stack,
    componentStack: errorInfo?.componentStack,
    originMessage: error.message,
    notify: false,
    showModal: false,
  };

  // Handle network errors - ALWAYS show connection error screen, never popup
  const isNetwork = isNetworkError(error);

  console.log('[ErrorBoundary] _processError - isNetworkError:', isNetwork);

  if (isNetwork) {
    console.log(
      '[ErrorBoundary] Network error detected - will show connection error screen'
    );
    return {
      ...base,
      message:
        'Unable to connect to server. Please check your internet connection and try again.',
      notify: false, // NEVER show popup
      showModal: true, // ALWAYS show connection error screen
    };
  }

  // Handle authentication errors
  if (error.message.includes('authentication is expired')) {
    return {
      ...base,
      message: 'Your session has expired. Please refresh the page to continue.',
      notify: true,
      showModal: false,
    };
  }

  // Handle API errors
  if (error.message.includes('Failed to get a response')) {
    return {
      ...base,
      message:
        'Unable to get a response from the server. Please try again later.',
      notify: true,
      showModal: true,
    };
  }

  // For any other errors, return a generic message
  return {
    ...base,
    message:
      'An unexpected error occurred. Please try again or contact support@cirra.ai if the issue persists.',
    notify: false,
    showModal: true,
  };
};

interface ErrorModalProps {
  errorDetails: ErrorDetails | null;
  visible: boolean;
  onClose: () => void;
  onReset: () => void;
}

const ErrorModal = ({
  errorDetails,
  visible,
  onClose,
  onReset,
}: ErrorModalProps) => {
  if (!errorDetails?.showModal) {
    return null;
  }

  const shouldShowStack = process.env.REACT_APP_TARGET_ENV === 'local';

  return (
    <Modal
      className="error-modal"
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
          <span>Error Details</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      footer={[]}
    >
      <div className="error-modal-content">
        <Result
          status="error"
          title="Something went wrong"
          subTitle={errorDetails.message}
        />
        <div className="error-modal-actions">
          <Button key="close" onClick={onClose}>
            Close
          </Button>
          <Button
            key="reload"
            type="primary"
            icon={<ReloadOutlined />}
            onClick={onReset}
          >
            Reload
          </Button>
        </div>

        <div className="error-details">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <ErrorField
              title="Error Message"
              content={errorDetails.originMessage}
            />
            {shouldShowStack && (
              <>
                <ErrorField
                  title="Stack Trace"
                  content={errorDetails.errorStack?.trim()}
                />
                <ErrorField
                  title="Component Stack"
                  content={errorDetails?.componentStack?.trim()}
                />
              </>
            )}
          </Space>
        </div>
      </div>
    </Modal>
  );
};

const ErrorFallback = () => {
  return null;
};

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

const ErrorBoundary = ({ children, fallback }: ErrorBoundaryProps) => {
  const [error, setError] = useState<Error | null>(null);
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null);
  const [errorDetails, setErrorDetails] = useState<ErrorDetails | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const handleError = (_error: Error, errorInfo: ErrorInfo) => {
    console.log('[ErrorBoundary] handleError called with:', _error);
    console.log(
      '[ErrorBoundary] Error type:',
      _error instanceof Error ? 'Error' : typeof _error
    );
    const errorObj = _error as { errorCode?: string; statusCode?: number };
    console.log('[ErrorBoundary] Error properties:', {
      message: _error.message,
      name: _error.name,
      errorCode: errorObj.errorCode,
      statusCode: errorObj.statusCode,
    });

    const _errorDetails = _processError(_error, errorInfo);
    console.log('[ErrorBoundary] Processed error details:', _errorDetails);
    setErrorDetails(_errorDetails);

    // Log error to Application Insights
    trackException(_error, { errorInfo });

    // Show error notification
    if (_errorDetails?.notify) {
      console.log(
        '[ErrorBoundary] Showing notification:',
        _errorDetails.message
      );
      Notify.error(_errorDetails.message);
    }

    // Show error modal
    if (_errorDetails?.showModal) {
      console.log('[ErrorBoundary] Showing modal');
      showErrorModal(_error, errorInfo);
    } else {
      console.log('[ErrorBoundary] NOT showing modal (showModal = false)');
    }
  };

  const showErrorModal = (_error: Error, errorInfo: ErrorInfo) => {
    // Don't show modal for canceled operations
    if (_error.name === 'CanceledError') {
      return;
    }

    setError(_error);
    setErrorInfo(errorInfo);
    setIsModalVisible(true);
  };

  const closeErrorModal = () => {
    setIsModalVisible(false);
  };

  const handleReset = () => {
    closeErrorModal();
    window.location.reload();
  };

  return (
    <>
      <ReactErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={handleError}
      >
        {children}
      </ReactErrorBoundary>

      {error && errorInfo && (
        <ErrorModal
          errorDetails={errorDetails}
          visible={isModalVisible}
          onClose={closeErrorModal}
          onReset={handleReset}
        />
      )}
    </>
  );
};

export default ErrorBoundary;
